// Phase 39.18 — Non-Technical animated courses catalogue.
// Add more courses by copying the schema below.
export const COURSES = {
  civic_sense: {
    name: 'Civic Sense — Being a Responsible Citizen',
    tagline: '14 missions · Learn by living the scenarios',
    color: 'from-emerald-500 to-teal-600',
    missions: [
      { type: 'intro', title: 'Why Civic Sense Matters', character: 'citizen', expression: 'talk',
        dialogue: "One person's careless act can spoil the whole street. But one thoughtful act can inspire hundreds.",
        points_list: [
          'Public spaces are shared — your action affects everyone.',
          'Small habits (using a bin, forming a queue) shape our city\'s character.',
          'Civic sense turns "my rights" into "our society".',
        ],
        wisdom: 'A civilised society is built one respectful act at a time.' },

      { type: 'concepts', title: 'Fundamentals of Civic Sense', character: 'teacher', expression: 'smile',
        dialogue: 'Civic sense = etiquette in public + respect for shared property + duty as a citizen.',
        points_list: [
          'Follow traffic rules — they save lives.',
          'Do not litter — every bin used is a cleaner city.',
          'Respect public property — it was built with everyone\'s tax money.',
          'Wait your turn — queues bring order.',
        ],
        wisdom: 'What you keep clean, others will keep clean too.' },

      { type: 'right_vs_wrong', title: 'Right vs Wrong: The Metro Escalator', character: 'student',
        dialogue: 'You are late for class. There\'s a rule: stand right, walk left.',
        wrong: 'Stand in the middle of the escalator with your bag blocking the walk-lane.',
        right: 'Stand on the right so people in a hurry can pass on the left.',
        wisdom: 'Small courtesies keep the city moving.' },

      { type: 'scenario', title: 'The Broken Streetlight', character: 'neighbour', expression: 'shock',
        dialogue: 'A streetlight has been out for a week. Kids fell twice. What do you do?',
        points_list: [
          'Observation: Broken infrastructure = safety risk.',
          'Options: Ignore, blame, or report.',
          'Decision: Report via the municipality app / helpline.',
          'Result: Fixed in 3 days · Neighbourhood safer.',
          'Lesson: Reporting is participation, not complaining.',
        ],
        wisdom: 'A citizen who reports a problem is halfway to solving it.' },

      { type: 'decision', title: 'What Would You Do?', character: 'student_f', expression: 'talk',
        dialogue: 'You finish a snack on the bus. There is no bin nearby. What do you do?',
        points: 15,
        badge: 'good_citizen',
        choices: [
          { text: 'Throw the wrapper out the bus window.', correct: false, reason: 'Someone will have to clean it — and it may harm animals or clog a drain.' },
          { text: 'Slip it under the seat.', correct: false, reason: 'Out of sight, but the cleaner and next passenger will find it.' },
          { text: 'Keep it in your pocket / bag until you find a bin.', correct: true, reason: 'You\'ve carried it 100 metres — you can carry it 100 more.' },
          { text: 'Ask another passenger to take it.', correct: false, reason: 'It\'s your wrapper — your responsibility.' },
        ],
        wisdom: 'The waste you carry until you find a bin is the vote you cast for a cleaner city.' },

      { type: 'rights_boundaries', title: 'Rights, Responsibilities & Boundaries', character: 'official',
        dialogue: 'Your rights end where another citizen\'s rights begin.',
        points_list: [
          'CAN: Use public spaces, speak freely, expect services.',
          'SHOULD: Keep queues, pay taxes, help those in distress.',
          'SHOULD NOT: Vandalise, litter, obstruct traffic.',
          'MUST RESPECT: Elders, disabled access, women\'s safety zones, silent zones.',
        ],
        wisdom: 'Freedom without responsibility is not liberty — it is chaos.' },

      { type: 'communication', title: 'Communication in Action', character: 'police', expression: 'talk',
        dialogue: 'A traffic constable stops you for a minor mistake. Words matter here.',
        right: 'Say "Sorry sir, I didn\'t notice — please guide me." Calm tone, accept correction.',
        wrong: 'Shout, argue, invoke your uncle\'s connections. Everyone loses time.',
        wisdom: 'Politeness is the shortest path to being understood.' },

      { type: 'emotional', title: 'Emotional & Social Intelligence', character: 'colleague', expression: 'sad',
        dialogue: 'Someone cuts you off in traffic. Your first reaction wants to scream.',
        points_list: [
          'PAUSE — count to 3 before reacting.',
          'CHECK — is this worth 30 minutes of anger?',
          'CHOOSE — a horn tap, a shrug, and move on.',
          'RESULT — you arrive safe and calm.',
        ],
        wisdom: 'Reacting is easy. Responding thoughtfully is what makes you a citizen.' },

      { type: 'community', title: 'Public & Community Behaviour', character: 'elder', expression: 'smile',
        dialogue: 'An elderly person boards the bus — every seat is taken.',
        right: 'Stand up. Offer the seat. Smile.',
        wrong: 'Pretend to look at your phone.',
        wisdom: 'A seat given to an elder today is a seat you receive tomorrow.' },

      { type: 'safety', title: 'Safety, Ethics & Responsibility', character: 'child', expression: 'shock',
        dialogue: 'You see a small child alone at the market, crying.',
        choices: [
          { text: 'Walk past — not my problem.', correct: false, reason: 'The child could face real danger. Doing nothing costs nothing to you, but everything to them.' },
          { text: 'Take the child home with you.', correct: false, reason: 'Well-meaning but risky — parents will panic; may look suspicious.' },
          { text: 'Stay with the child, alert a shopkeeper / guard / police helpline.', correct: true, reason: 'Safest, kindest, most legal — you\'re a bridge, not a rescuer.' },
          { text: 'Give the child money and leave.', correct: false, reason: 'Doesn\'t address the danger.' },
        ],
        points: 20,
        badge: 'community_hero',
        wisdom: 'Doing the right thing rarely takes more than 5 minutes.' },

      { type: 'digital', title: 'Digital Citizenship', character: 'student', expression: 'talk',
        dialogue: 'A dramatic WhatsApp forward lands in your family group. Share now?',
        points_list: [
          'CHECK the source before forwarding.',
          'SEARCH the headline on a news site.',
          'ASK: could this hurt someone if it\'s false?',
          'REPORT and DO NOT forward if unsure.',
        ],
        wisdom: 'You are not the message — you are the moment of pause before the forward.' },

      { type: 'simulation', title: 'Challenge Simulation: A Day in the City', character: 'citizen',
        dialogue: 'One day. Multiple choices. Score every good decision.',
        choices: [
          { text: 'Return the ₹500 note that fell from the shopkeeper\'s counter.', correct: true, reason: '+15 XP: honesty.' },
          { text: 'Move aside to let an ambulance pass — even though the light is green.', correct: true, reason: '+15 XP: humanity.' },
          { text: 'Take a selfie inside the museum with flash.', correct: false, reason: 'Damages art. Read the signs first.' },
        ],
        points: 15,
        badge: 'critical_thinker',
        wisdom: 'A day of good choices adds up to a life of good character.' },

      { type: 'be_the_change', title: 'Be the Change', character: 'hero', expression: 'smile',
        dialogue: 'One clean street inspires a clean neighbourhood inspires a clean city.',
        points_list: [
          'Pick up ONE piece of trash today — even if it isn\'t yours.',
          'Compliment ONE stranger who did something civic (e.g. waited in queue).',
          'Report ONE broken civic thing to the right authority.',
        ],
        wisdom: 'Change begins the moment one person decides to act — let it be you.' },

      { type: 'action_plan', title: 'My Action Plan', character: 'citizen', expression: 'talk',
        dialogue: 'Pick 3 promises you will keep for the next 30 days.',
        choices: [
          { text: 'I will always use a bin (or pocket) for wrappers.', correct: true, reason: 'A city with 100 people who do this is a cleaner city.' },
          { text: 'I will let ambulances pass — always.', correct: true, reason: 'Someone\'s parent is inside.' },
          { text: 'I will not forward unverified WhatsApp forwards.', correct: true, reason: 'You\'ve just protected your family from panic.' },
          { text: 'I will offer my seat to elders on public transport.', correct: true, reason: 'Legs get tired; kindness never does.' },
        ],
        points: 25,
        badge: 'good_citizen',
        wisdom: 'A promise kept for 30 days becomes a habit for life.' },
    ],
    quiz: [
      { q: 'You see a stray plastic bottle on the footpath. What is the best thing to do?',
        opts: ['Kick it aside', 'Pick it up and put it in a bin', 'Post about it online', 'Leave it — someone paid to clean will find it'],
        correct: 1, explain: 'Small clean-up actions inspire others.' },
      { q: 'An ambulance is behind you with siren on. Light is green. What do you do?',
        opts: ['Speed up so the ambulance can follow', 'Move to the left / edge and let it pass', 'Stop right where you are', 'Honk back'],
        correct: 1, explain: 'Move aside safely — never block emergency vehicles.' },
      { q: 'A forward on WhatsApp claims a rumour about a communal event. Best action?',
        opts: ['Forward to alert family', 'Verify from at least one trusted news site first', 'Screenshot and post on Instagram', 'Delete the group'],
        correct: 1, explain: 'Verify before you multiply.' },
      { q: 'Elderly neighbour asks for help paying an online bill. You?',
        opts: ['Politely help them', 'Ignore, not your problem', 'Charge a fee', 'Tell them to figure it out'],
        correct: 0, explain: 'Small help across generations is the fabric of community.' },
      { q: 'The public park bench is broken. Who is responsible for reporting it?',
        opts: ['Only the municipality', 'The last person who used it', 'You, if you see it — via a citizen app / helpline', 'Nobody'],
        correct: 2, explain: 'Reporting is participation.' },
      { q: 'Priority-seat rule on the bus applies to…',
        opts: ['Only VIPs', 'Elderly, disabled, pregnant women, women with children', 'The first person to sit', 'Nobody'],
        correct: 1, explain: 'Priority seats exist for people who need them most.' },
      { q: 'You witness someone spitting on a heritage monument. Best step?',
        opts: ['Shout and start a fight', 'Politely remind them + inform the on-site guard', 'Take a video and post publicly for shame', 'Do nothing'],
        correct: 1, explain: 'Correction with respect is the mature response.' },
      { q: 'A stranger asks you for your Aadhaar OTP over the phone.',
        opts: ['Share — they said they\'re from the bank', 'Refuse and hang up', 'Share only 3 digits', 'Send them a screenshot'],
        correct: 1, explain: 'Never share OTP. Real banks never ask for it.' },
      { q: 'Traffic signal turns red while you are 5 metres away.',
        opts: ['Speed up and cross', 'Stop safely before the line', 'Honk and cross', 'Ask other drivers to slow down'],
        correct: 1, explain: 'One skipped signal can end a life.' },
      { q: 'The passing score for this quiz to earn a certificate is:',
        opts: ['50%', '60%', '70%', '90%'],
        correct: 2, explain: 'Aim for 70% or higher.' },
    ],
  },

  // Add life_skills, ethics, financial_literacy etc. here — same schema.
};

export const COURSE_LIST = Object.entries(COURSES).map(([id, c]) => ({
  id, name: c.name, tagline: c.tagline, color: c.color, missions: c.missions.length,
}));
